import React, { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import PageTitle from "../../components/pagetitle/PageTitle";
import Header from "../../components/header/Header";
import CtaSection from "../../components/CtaSection/CtaSection";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";

function Privacy() {
  const [privacyPolicy, setPrivacyPolicy] = useState(null);
  const [seo, setSeo] = useState(null);

  // Fetch Privacy
  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        const res = await axios.get("https://api.rashtechinfo.com/activeTnC", {
          withCredentials: true,
        });
        const privacy = res?.data?.data?.privacy?.[0] || null;
        setPrivacyPolicy(privacy);
      } catch (error) {
        console.error("Error fetching Privacy Policy:", error);
      }
    };
    fetchPrivacyPolicy();
  }, []);

  // Fetch site-wide SEO and try to match this route
  useEffect(() => {
    const fetchSeo = async () => {
      try {
        const res = await axios.get("https://api.rashtechinfo.com/getSeo");
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        // Try to pick a matching SEO entry, else fallback to null
        const routeKey = "privacy";
        const match =
          list.find((s) => s?.page?.name?.toLowerCase() === routeKey) ||
          list.find((s) => "privacy".includes(s?.page?.name?.toLowerCase() || "")) ||
          null;
        setSeo(match);
      } catch (e) {
        // Non-blocking
      }
    };
    fetchSeo();
  }, []);

  const computed = useMemo(() => {
    const staticMeta = {
      title: "Privacy Policy | RashTech Info",
      description:
        "Read RashTech Info’s Privacy Policy to understand how data is collected, used, and protected.",
      url: "https://rashtechinfo.com/privacy",
      image: "https://rashtechinfo.com/og-image.png",
      siteName: "RashTech Info",
      keywords: "privacy policy, data protection, terms, RashTech Info",
      themeColor: "#0A0D1C",
    };

    const dynamic = {
      title: seo?.title,
      description: seo?.metaDescription,
      keywords: seo?.metaKeywords,
      canonicalUrl: seo?.canonicalUrl,
    };

    return {
      title: dynamic.title || staticMeta.title,
      description: dynamic.description || staticMeta.description,
      keywords: dynamic.keywords || staticMeta.keywords,
      url: dynamic.canonicalUrl || staticMeta.url,
      image: staticMeta.image,
      siteName: staticMeta.siteName,
      themeColor: staticMeta.themeColor,
    };
  }, [seo]);

  if (!privacyPolicy) {
    return (
      <Fragment>
        <Header />
        <main className="page_content about-page">
          <div className="container py-5 text-center">Loading Privacy Policy...</div>
        </main>
        <Footer />
        <Scrollbar />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>{computed.title}</title>
        <meta name="description" content={computed.description} />
        {computed.keywords && <meta name="keywords" content={computed.keywords} />}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={computed.themeColor} />
        {computed.url && <link rel="canonical" href={computed.url} />}

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={computed.siteName} />
        <meta property="og:title" content={computed.title} />
        <meta property="og:description" content={computed.description} />
        {computed.url && <meta property="og:url" content={computed.url} />}
        {computed.image && <meta property="og:image" content={computed.image} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={computed.title} />
        <meta name="twitter:description" content={computed.description} />
        {computed.image && <meta name="twitter:image" content={computed.image} />}
      </Helmet>

      <Header />
      <main className="page_content about-page">
        <PageTitle pageTitle={"Privacy Policy"} pagesub={"Policy"} pageTop={"Privacy &"} />
        <section className="section_space">
          <div className="container">
            {(privacyPolicy?.headings || []).map((section, idx) => (
              <div key={idx} className="mb-5">
                {section.title && <h2 className="text-2xl font-bold mb-3">{section.title}</h2>}
                <div
                  className="blog-content prose"
                  // Ensure your backend sanitizes HTML or sanitize before rendering, if needed
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              </div>
            ))}
          </div>
        </section>
      </main>
      <CtaSection />
      <Footer />
      <Scrollbar />
    </Fragment>
  );
}

export default Privacy;
